import { createClient } from '@supabase/supabase-js';

const runtimeEnv = (window as any)._env_ || {};
const supabaseUrl = runtimeEnv.REACT_APP_SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = runtimeEnv.REACT_APP_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    const parts = token.split('.');

    if (parts.length < 2 || typeof window.atob !== 'function') {
        return null;
    }

    try {
        const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
        return JSON.parse(window.atob(normalized + padding));
    } catch {
        return null;
    }
};

const extractProjectRef = (url: string): string | null => {
    try {
        return new URL(url).hostname.split('.')[0] || null;
    } catch {
        return null;
    }
};

const isPlaceholderValue = (value: string): boolean => {
    const normalized = value.trim();
    return normalized.length === 0
        || normalized === 'SEU_ANON_KEY_AQUI'
        || normalized === 'your-anon-key-here'
        || normalized.endsWith('...')
        || normalized.includes('COLE_AQUI');
};

const validateSupabaseConfig = (): void => {
    if (!supabaseUrl) {
        throw new Error('Supabase URL is missing. Define REACT_APP_SUPABASE_URL in the build or in config.js.');
    }

    if (isPlaceholderValue(supabaseAnonKey)) {
        throw new Error('Supabase anon key is missing or still contains a placeholder/truncated value.');
    }

    const projectRef = extractProjectRef(supabaseUrl);
    const tokenPayload = decodeJwtPayload(supabaseAnonKey);
    const tokenRefValue = tokenPayload ? tokenPayload['ref'] : null;
    const tokenRef = typeof tokenRefValue === 'string' ? tokenRefValue : null;

    if (projectRef && tokenRef && projectRef !== tokenRef) {
        throw new Error(`Supabase anon key does not match REACT_APP_SUPABASE_URL. Expected project "${projectRef}", got key for "${tokenRef}".`);
    }
};

validateSupabaseConfig();

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const fetchLeads = async () => {
    const { data, error } = await supabase
        .from('Cadastro_Clientes')
        .select('empreendimento');
    if (error) {
        throw new Error(error.message);
    }

    // Agrupar dados manualmente no frontend
    const grouped = data?.reduce((acc: any, item: any) => {
        const empreendimento = item.empreendimento;
        if (!acc[empreendimento]) {
            acc[empreendimento] = 0;
        }
        acc[empreendimento]++;
        return acc;
    }, {});

    return Object.entries(grouped || {}).map(([empreendimento, count]) => ({
        empreendimento,
        count
    }));
};

export const fetchSchedules = async () => {
    const { data, error } = await supabase
        .from('Cadastro_Clientes')
        .select('created_at, agendamento')
        .order('created_at', { ascending: true });
    if (error) {
        throw new Error(error.message);
    }
    return data;
};

export const fetchLeadsByBroker = async () => {
    const { data, error } = await supabase
        .from('Cadastro_Clientes')
        .select('corretor_responsavel');
    if (error) {
        throw new Error(error.message);
    }

    // Agrupar dados manualmente no frontend
    const grouped = data?.reduce((acc: any, item: any) => {
        const corretor = item.corretor_responsavel;
        if (!acc[corretor]) {
            acc[corretor] = 0;
        }
        acc[corretor]++;
        return acc;
    }, {});

    return Object.entries(grouped || {}).map(([corretor_responsavel, count]) => ({
        corretor_responsavel,
        count
    }));
};

export default supabase;
